/**
 * Spreadsheet canvas — viewer for `kind: "spreadsheet"` artifacts.
 *
 * Renders the parsed CSV body inside a scrollable table with a pinned
 * header row and right-aligned tabular numerics. Streaming deltas reach
 * the store and re-parse on every render — the parser tolerates partial
 * trailing rows so the user sees the table grow as the model writes.
 *
 * @module components/artifacts/spreadsheet-canvas
 */

import { motion } from "framer-motion";
import { Copy, Download, Maximize2, Table2, X } from "lucide-react";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatCount, parseCsv, type ParsedCsv } from "@/lib/artifacts/csv";
import { useArtifactStore } from "@/store/artifact-store";
import { ArtifactErrorBanner } from "./artifact-error-banner";
import { FocusModeToggle } from "./focus-mode-toggle";
import { GenerationSkeleton } from "./generation-skeleton";
import { SpreadsheetGrid } from "./spreadsheet-grid";

/**
 * Standalone spreadsheet canvas. Mounts via the kind dispatcher in
 * `artifact.tsx`; reads body + status from the global store.
 */
export function SpreadsheetCanvas() {
  const artifact = useArtifactStore((s) => s.artifact);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const isStreaming = artifact.status === "streaming";
  const isUpdateInFlight = artifact.pendingContent !== null;
  /**
   * The CSV body we parse.
   *
   * - **Create**: deltas land in `artifact.content` row-by-row — the parser
   *   tolerates partial trailing rows so the grid grows as bytes arrive.
   * - **Update**: deltas land in `artifact.pendingContent`. Point the parser
   *   at it so the user sees the new sheet building up live (same UX as a
   *   fresh create) instead of a static "Updating…" overlay. When
   *   `finishStream` swaps pendingContent → content, `isUpdateInFlight`
   *   flips false and we resolve back to `content` — same string, no
   *   visible interruption.
   */
  const sourceCsv = isUpdateInFlight
    ? (artifact.pendingContent ?? "")
    : artifact.content;
  const parsed = useMemo<ParsedCsv>(() => parseCsv(sourceCsv), [sourceCsv]);
  const hasRows = parsed.headers.length > 0;

  const handleCopyCsv = useCallback(async () => {
    if (!artifact.content) return;
    try {
      await navigator.clipboard.writeText(artifact.content);
      toast.success("CSV copied to clipboard");
    } catch {
      toast.error("Could not copy CSV");
    }
  }, [artifact.content]);

  const handleDownloadCsv = useCallback(() => {
    if (!artifact.content) return;
    const blob = new Blob([artifact.content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(artifact.title)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [artifact.content, artifact.title]);

  const handleFullscreen = useCallback(() => {
    if (!artifact.content) return;
    const blob = new Blob([artifact.content], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    // Don't revoke — the new tab needs the URL to fetch the blob.
  }, [artifact.content]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 80, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex h-full min-w-0 flex-[3] flex-col border-l bg-background shadow-2xl max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex-none max-lg:border-l-0"
    >
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
        <div className="size-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Table2 className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold leading-tight">
            {artifact.title || "Untitled"}
          </h2>
          <SubtitleRow
            isStreaming={isStreaming}
            isUpdateInFlight={isUpdateInFlight}
            rowCount={parsed.rows.length}
            columnCount={parsed.headers.length}
          />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleCopyCsv}
          disabled={!hasRows}
          title="Copy CSV"
        >
          <Copy className="size-5" />
        </Button>
        <FocusModeToggle />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleDownloadCsv}
          disabled={!hasRows}
          title="Download CSV"
        >
          <Download className="size-5" />
        </Button>
        <div className="mx-1 h-5 w-px bg-border" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleFullscreen}
          disabled={!hasRows}
          title="Open in new tab"
        >
          <Maximize2 className="size-5" />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={hideArtifact} title="Close">
          <X className="size-5" />
        </Button>
      </header>

      <div className="relative flex-1 min-h-0 min-w-0 overflow-hidden bg-muted/10">
        {hasRows ? (
          <SpreadsheetGrid parsed={parsed} className="absolute inset-0" />
        ) : isStreaming || isUpdateInFlight ? (
          <GenerationSkeleton kind="spreadsheet" title={artifact.title} />
        ) : (
          <EmptyState />
        )}
      </div>

      {/*
        * Updates stream into the same skeleton+grid path as creates
        * (see `sourceCsv` above), so we don't mount the full-screen
        * `ArtifactUpdateOverlay`. The error/retry banner stays.
        */}
      <ArtifactErrorBanner />
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
      <Table2 className="size-10 text-muted-foreground/50" />
      <div className="max-w-xs text-sm text-muted-foreground">
        This spreadsheet is empty — the producer hasn't written any rows yet.
      </div>
    </div>
  );
}

function SubtitleRow({
  isStreaming,
  isUpdateInFlight,
  rowCount,
  columnCount,
}: {
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  rowCount: number;
  columnCount: number;
}) {
  const parts: string[] = ["Spreadsheet"];
  if (columnCount > 0) {
    parts.push(formatCount(rowCount, "row"));
    parts.push(formatCount(columnCount, "column"));
  }
  if (isUpdateInFlight) parts.push("Updating…");
  else if (isStreaming) parts.push("Generating…");
  return <span className="text-xs text-muted-foreground">{parts.join(" · ")}</span>;
}

function safeFilename(title: string): string {
  const slug = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
  return slug || "spreadsheet";
}
